<template>
  <form @submit.prevent="submit">
    <!-- select what type of package you want (small, medium, large, etc) -->
    <div v-if="showPackages()" class="mb-4">
      <FormDevSitePackageSelect
        v-model="fields.packageId"
        :packages="getPackages()"
        :plan-term="getPlanTerm()"
      />
    </div>

    <div class="text-center text-secondary mb-4">
      {{ $t("global.dev-site-creation-description") }}
    </div>

    <FormCheckbox
      name="copy-data"
      v-model="fields.copyData"
      :label="`${$t('global.copy-site-data', { environment: environmentType })}`"
    />

    <div v-if="fields.copyData" class="px-4">
      <Alert
        :message="$t('global.copy-site-data-warning', { environment: environmentType })"
        variant="warning"
        :dismissible="false"
      />
      <FormCheckbox
        name="copy-data"
        v-model="fields.copyUserData"
        :label="`${$t('global.copy-site-user-data')}`"
      />
    </div>
    <div class="form-buttons">
      <Btn
        class="mr-2"
        variant="light"
        type="button"
        :disabled="loading"
        @click="cancel"
        data-test="cancel-create-dev-site-button"
      >
        {{ $t("global.cancel") }}
      </Btn>
      <Btn
        variant="primary"
        type="submit"
        :disabled="!isSubmittable"
        :loading="loading"
        data-test="create-dev-site-button"
      >
        {{ $t("global.create") }}
      </Btn>
    </div>
  </form>
</template>

<script>
import { computed, reactive, ref } from "@vue/composition-api";
import Alert from "@/components/display/Alert";
import Btn from "@/components/display/Btn";
import FormCheckbox from "@/components/display/form/FormCheckbox";
import FormDevSitePackageSelect from "@/components/display/form/FormDevSitePackageSelect";
import store from "@/store";

export default {
  name: "DevSiteAddForm",
  components: {
    Alert,
    Btn,
    FormCheckbox,
    FormDevSitePackageSelect,
  },
  props: {
    plan: {
      type: Object,
      required: true,
    },
    site: {
      type: Object,
    },
    environmentType: {
      type: String,
      required: true,
      validator: (value) => {
        return ["staging", "development"].includes(value);
      },
    },
    packages: {
      type: Array,
      required: true,
    },
    package: Object,
    locationId: Number,
    safeHarbor: {
      type: String,
      validator: (value) => (value ? ["shared", "site"].includes(value) : true),
    },
  },
  setup(props, { emit, root }) {
    const errors = ref(null);
    const loading = ref(false);

    // default select the first version in the list
    const fields = reactive({
      environmentType: props.environmentType,
      copyData: true,
      copyUserData: true,
      packageId: props.package?.id,
      siteId: props.site?.id,
      planId: props.plan.id,
      locationId: props.locationId,
    });

    const cancel = () => emit("cancel");

    const submit = () => {
      errors.value = null;
      loading.value = true;
      if (!fields.copyData) fields.copyUserData = false;
      store
        .dispatch("devSites/create", { siteId: props.site?.id, data: fields })
        .then(() => {
          store.dispatch("toasts/addToast", {
            variant: "info",
            message: root.$t("toasts.dev-site-creating-message", {
              environmentType: props.environmentType,
              name: props.site?.domain,
            }),
          });
          if (props.environmentType === "staging" && props.safeHarbor === "shared") {
            store.dispatch("safeHarbor/addStagingEnvironmentUpdate", props.plan.id);
          }
          emit("success");
        })
        .catch(() => {
          store.dispatch("toasts/addToast", {
            variant: "info",
            message: root.$t("toasts.dev-site-creating-error-message", {
              environmentType: props.environmentType,
              name: props.site?.domain,
            }),
          });
        })
        .finally(() => {
          loading.value = false;
        });
    };

    const isSelected = ({ id }) => fields.id === id;
    const isSubmittable = computed(() => !!fields.packageId || !props.site);
    const getPlanTerm = () => (props.plan.billing.isAnnualTerm ? 12 : 1);
    const showPackages = () => !props.package && props.site && !props.safeHarbor;
    const getPackages = () => {
      if (props.package) {
        return [props.package];
      }

      return props.packages;
    };

    return {
      errors,
      loading,
      fields,
      submit,
      cancel,
      isSelected,
      isSubmittable,
      getPlanTerm,
      showPackages,
      getPackages,
    };
  },
};
</script>
